import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useFormContext, useWatch } from 'react-hook-form';
import RevertIconButton from '@/components/revert-icon-button/revert-icon-button';

const StyledFieldWrapper = styled.div`
  position: relative;
  width: ${(props) => (props.$inlineRevert ? 'auto' : '100%')};
  display: ${(props) => (props.$inlineRevert ? 'inline-block' : 'block')};
  float: ${(props) => (props.$inlineRevert ? 'right' : 'none')};
`;

const StyledRevertButtonWrapper = styled.div`
  position: absolute;
  right: ${(props) => props.$right || '8px'};
  top: ${(props) => props.$top || '50%'};
  transform: translateY(${(props) => props.$translateY || '-50%'});
  z-index: 10;
`;

const StyledInlineRevertButtonWrapper = styled.div`
  position: relative;
  display: ${(props) => (props.$inlineRevert ? 'inline-block' : 'block')};
  z-index: 10;
`;

/**
 * FieldWithRevert component
 * @param {object} props - The props to use.
 * @param {JSX.Element} props.children - The children to use.
 * @param {string} props.fieldName - The field name to use.
 * @param {string} props.tooltipMessage - The tooltip message to use.
 * @param {string} props.right - The right position to use.
 * @param {string} props.top - The top position to use.
 * @param {string} props.translateY - The translateY position to use.
 * @param {Function} props.handleRevert - The handle revert function to use.
 * @param {boolean} props.inlineRevert - Whether to inline the revert button.
 * @returns {JSX.Element} The rendered FieldWithRevert component.
 */
const FieldWithRevert = ({
  children,
  fieldName,
  tooltipMessage,
  right,
  top,
  translateY,
  inlineRevert,
  handleRevert,
}) => {
  const { control } = useFormContext();
  const showRevert = useWatch({
    control,
    name: `product.overrides.${fieldName}`,
  });

  const RevertButtonWrapper = inlineRevert
    ? StyledInlineRevertButtonWrapper
    : StyledRevertButtonWrapper;

  return (
    <StyledFieldWrapper $inlineRevert={inlineRevert}>
      {children}
      {showRevert && (
        <RevertButtonWrapper
          $right={right}
          $top={top}
          $translateY={translateY}
          $inlineRevert={inlineRevert}
        >
          <RevertIconButton
            onClick={() => handleRevert(fieldName)}
            tooltipMessage={tooltipMessage}
          />
        </RevertButtonWrapper>
      )}
    </StyledFieldWrapper>
  );
};

FieldWithRevert.propTypes = {
  fieldName: PropTypes.string.isRequired,
  tooltipMessage: PropTypes.string.isRequired,
  right: PropTypes.string,
  top: PropTypes.string,
  translateY: PropTypes.string,
  handleRevert: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  inlineRevert: PropTypes.bool,
};

FieldWithRevert.defaultProps = {
  right: '8px',
  top: '50%',
  translateY: '-50%',
  inlineRevert: false,
};

export default FieldWithRevert;
